import message from 'antd/lib/message';
import { CloseIcon } from 'components/icons';
import { defaultApiErrorSchema } from 'lib/api/utils';
import { useApproveOrRejectPendingOrder } from 'lib/hooks/order/order.mutations';
import React from 'react';
import ReactModal from 'react-modal';
import { OrderAction } from 'types/shared';
import { Order } from '../RecentEsimOrdersList/RecentEsimOrdersList';

type ApproveEsimOrderModalProps = {
  isOpen: boolean;
  onClose: () => void;
  selectedOrder: Order | null;
};

function ApproveEsimOrderModal({
  isOpen,
  onClose,
  selectedOrder,
}: ApproveEsimOrderModalProps) {
  const [errorMessage, setErrorMessage] = React.useState('');
  const { isLoading, mutateAsync: approveOrder } =
    useApproveOrRejectPendingOrder();

  const closeModalHandler = () => {
    setErrorMessage('');
    onClose();
  };

  const approveOrderHandler = async () => {
    if (!selectedOrder) return;

    setErrorMessage('');

    try {
      await approveOrder({
        orderNumber: selectedOrder.orderNumber,
        action: OrderAction.Approve,
      });

      closeModalHandler();

      message.success({ key: 'updatable', content: 'Order approved' });
    } catch (e) {
      const errorMessage =
        defaultApiErrorSchema.safeParse(e).data?.data?.message ??
        'Error trying to approve order';

      setErrorMessage(errorMessage);
    }
  };

  const isSubmitDisabled = isLoading || !selectedOrder;

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={closeModalHandler}
      ariaHideApp={false}
      contentLabel="Are you sure to approve this order?"
      portalClassName="react-modal"
      overlayClassName="modal"
      className="modal-dialog react-modal-dialog-sm modal-dialog-centered"
    >
      <div className="modal-content">
        <div
          className="react-modal-header"
          style={{ padding: 24, paddingBottom: 16 }}
        >
          <div>
            <h5 className="react-modal-title react-modal-title-md">
              Are you sure you want to approve this order?
            </h5>

            <h6
              className="react-modal-subtitle"
              style={{ fontSize: 14, marginTop: 6, color: '#595959' }}
            >
              Order Number:{' '}
              <span className="font-weight-bold">
                {selectedOrder?.orderNumber}
              </span>
            </h6>
          </div>

          <button
            type="button"
            className="btn react-modal-close"
            onClick={closeModalHandler}
          >
            <CloseIcon className={''} />
          </button>
        </div>

        <div className="react-modal-body">
          <p className="react-modal-subtitle">
            Approving this order will allocate{' '}
            <span className="font-weight-bold">
              {selectedOrder?.orderedSims}
            </span>{' '}
            eSIMS to partner and complete the order. Are you sure you want to
            proceed?
          </p>

          {!!errorMessage && (
            <span className="invalid-feedback d-block mt-2">
              {errorMessage}
            </span>
          )}

          <div className="text-right mr-2 mt-4">
            <button
              onClick={approveOrderHandler}
              className="btn btn-dark btn-sm w-250 mr-2"
              disabled={isSubmitDisabled}
            >
              {isLoading ? 'Please Wait...' : 'Yes'}
            </button>
            <button
              onClick={closeModalHandler}
              className="btn btn-dark btn-sm w-250"
              disabled={isLoading}
            >
              No
            </button>
          </div>
        </div>
      </div>
    </ReactModal>
  );
}

export default ApproveEsimOrderModal;
